import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import TableComponent from "../../components/TableComponent";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import {
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  fetchCategoryByType,
  fetchCategoryByTypePublic,
} from "../../features/admin/CategorySlice";
import EventInvitationCardTemplateEditor, {
  createEventInvitationImageLayer,
  createEventInvitationTextLayer,
  ensureEventInvitationSchemaV2,
  safeParseEventInvitationSchema,
} from "../../components/admin/EventInvitationCardTemplateEditor";

const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

/* ---------- Permission helpers ---------- */
const isSuperAdmin = () => String(localStorage.getItem("userId")) === "1";
const getUserPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    return raw ? JSON.parse(raw).map((p) => p.permissionSlug) : [];
  } catch {
    return [];
  }
};
const hasPermission = (slug) =>
  isSuperAdmin() || getUserPermissions().includes(slug);

/* ---------- UI primitives ---------- */
const Input = (props) => (
  <input
    {...props}
    className={`input input-bordered w-full ${props.className || ""}`}
  />
);

const Select = (props) => (
  <select
    {...props}
    className={`select select-bordered w-full ${props.className || ""}`}
  />
);

const Modal = ({ isOpen, title, onClose, children, footer }) => {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="relative bg-white w-full max-w-7xl mx-2 rounded-2xl p-6 shadow-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            title="Đóng"
          >
            ✕
          </button>
        </div>
        <div className="space-y-4">{children}</div>
        {footer && <div className="mt-6">{footer}</div>}
      </div>
    </div>
  );
};

/* ---------- API helpers ---------- */
const extractList = (res) => {
  const payload = res?.data?.data ?? res?.data ?? null;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const extractDetail = (res) => res?.data?.data ?? res?.data ?? null;

const EventInvitationCardTemplates = () => {
  const dispatch = useDispatch();
  const { categories = [] } = useSelector((state) => state.categories);

  const canView = hasPermission("EVENT_INVITATION_CARD_SHOW");
  const canCreate = hasPermission("EVENT_INVITATION_CARD_CREATE");
  const canUpdate = hasPermission("EVENT_INVITATION_CARD_UPDATE");
  const canDelete = hasPermission("EVENT_INVITATION_CARD_DELETE");

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);

  const [search, setSearch] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("all");

  const [confirm, setConfirm] = useState({ open: false, id: null });

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const [metaForm, setMetaForm] = useState({
    categoryId: "",
    code: "",
    name: "",
    version: 1,
    isActive: true,
  });

  const [schema, setSchema] = useState(() =>
    ensureEventInvitationSchemaV2(null),
  );

  const eventCategories = useMemo(
    () => (Array.isArray(categories) ? categories : []),
    [categories],
  );

  const loadTemplates = async () => {
    if (!canView) return;
    setLoading(true);

    try {
      const params = {};

      if (search?.trim()) params.s = search.trim();

      if (filterCategoryId === "__default__") {
        params.categoryId = "";
      } else if (filterCategoryId !== "all") {
        params.categoryId = filterCategoryId;
      }

      const res = await ApiCustom.get("/event-invitation-card-template-show", {
        params,
      });
      const items = extractList(res);
      setTemplates(Array.isArray(items) ? items : []);
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          "Không tải được danh sách mẫu thiệp sự kiện",
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const userId = localStorage.getItem("userId");
    if (Number(userId) === 1) {
      dispatch(fetchCategoryByTypePublic("notification"));
    } else {
      dispatch(fetchCategoryByType("notification"));
    }
  }, [dispatch]);

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterCategoryId]);

  const getCategoryName = (item) => {
    if (!item?.categoryId) return "Mặc định";

    return (
      item?.category?.name ||
      eventCategories.find((cate) => `${cate.id}` === `${item.categoryId}`)
        ?.name ||
      `#${item.categoryId}`
    );
  };

  const openCreate = () => {
    if (!canCreate) return;

    setEditing(null);
    setMetaForm({
      categoryId: "",
      code: "",
      name: "",
      version: 1,
      isActive: true,
    });
    setSchema(ensureEventInvitationSchemaV2(null));
    setSelected(null);
    setEditorOpen(true);
  };

  const openEdit = async (item) => {
    if (!canUpdate) return;

    try {
      const res = await ApiCustom.get(
        `/event-invitation-card-template-show-id/${item.id}`,
      );
      const detail = extractDetail(res) || item;

      setEditing(detail);
      setMetaForm({
        categoryId: detail.categoryId ? String(detail.categoryId) : "",
        code: detail.code || "",
        name: detail.name || "",
        version: Number(detail.version ?? 1),
        isActive: Boolean(detail.isActive ?? true),
      });

      const loaded = ensureEventInvitationSchemaV2(
        safeParseEventInvitationSchema(
          safeParseEventInvitationSchema(detail.schema),
        ),
      );

      setSchema(loaded);
      setSelected(null);
      setEditorOpen(true);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Không lấy được chi tiết mẫu thiệp",
      );
    }
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditing(null);
    setSelected(null);
  };

  const saveTemplate = async () => {
    const payload = {
      categoryId: metaForm.categoryId ? Number(metaForm.categoryId) : null,
      code: String(metaForm.code || "").trim(),
      name: String(metaForm.name || "").trim(),
      version: Number(metaForm.version) || 1,
      isActive: Boolean(metaForm.isActive),
      schema: JSON.stringify(ensureEventInvitationSchemaV2(schema)),
    };

    if (!payload.code) return toast.error("Vui lòng nhập mã");
    if (!payload.name) return toast.error("Vui lòng nhập tên mẫu");

    try {
      if (editing?.id) {
        await ApiCustom.put(
          `/event-invitation-card-template-update/${editing.id}`,
          payload,
          JSON_HEADERS,
        );
        toast.success("Cập nhật mẫu thiệp sự kiện thành công");
      } else {
        await ApiCustom.post(
          "/event-invitation-card-template-create",
          payload,
          JSON_HEADERS,
        );
        toast.success("Tạo mẫu thiệp sự kiện thành công");
      }

      closeEditor();
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lỗi khi lưu mẫu thiệp");
    }
  };

  const requestDelete = (id) => {
    if (!canDelete) return;
    setConfirm({ open: true, id });
  };

  const doDelete = async () => {
    if (!canDelete) return;

    try {
      await ApiCustom.delete(
        `/event-invitation-card-template-delete/${confirm.id}`,
        JSON_HEADERS,
      );
      toast.success("Đã xoá mẫu thiệp sự kiện");
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Xoá thất bại");
    } finally {
      setConfirm({ open: false, id: null });
    }
  };

  const resetSchema = () => {
    setSchema(ensureEventInvitationSchemaV2(null));
    setSelected(null);
    toast.info("Đã reset về mẫu mặc định");
  };

  const addText = () => {
    setSchema((prev) => {
      const next = ensureEventInvitationSchemaV2(prev);
      const newLayer = createEventInvitationTextLayer({
        zIndex: (next.layers?.length || 0) + 50,
      });

      next.layers = [...(next.layers || []), newLayer];
      return next;
    });

    toast.info("Đã thêm chữ.");
  };

  const addImage = () => {
    setSchema((prev) => {
      const next = ensureEventInvitationSchemaV2(prev);
      const newLayer = createEventInvitationImageLayer({
        zIndex: (next.layers?.length || 0) + 50,
      });

      next.layers = [...(next.layers || []), newLayer];
      return next;
    });

    toast.info("Đã thêm ảnh.");
  };

  const headers = useMemo(() => {
    const base = [
      {
        key: "id",
        label: "STT",
        render: (_, index) => <span>{index + 1}</span>,
      },
      {
        key: "categoryId",
        label: "Danh mục hoạt động",
        render: (item) => <span>{getCategoryName(item)}</span>,
      },
      { key: "code", label: "Mã" },
      { key: "name", label: "Tên mẫu" },
      { key: "version", label: "Phiên bản" },
      {
        key: "isActive",
        label: "Trạng thái",
        render: (item) => (
          <span
            className={`badge ${
              item?.isActive ? "badge-success" : "badge-ghost"
            }`}
          >
            {item?.isActive ? "Đang dùng" : "Tắt"}
          </span>
        ),
      },
    ];

    if (canUpdate || canDelete) {
      base.push({ key: "setting", label: "Phương thức" });
    }

    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUpdate, canDelete, eventCategories]);

  const settings = {
    renderSettings: (item) => (
      <div className="flex items-center gap-2">
        {canUpdate && (
          <button
            onClick={() => openEdit(item)}
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
            title="Sửa"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>
        )}

        {canDelete && (
          <button
            onClick={() => requestDelete(item.id)}
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            title="Xoá"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  if (!canView) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Cài đặt thiệp sự kiện</h1>
        <div className="alert alert-warning">
          Bạn không có quyền truy cập chức năng này.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Cài đặt thiệp sự kiện</h1>

      <div className="flex justify-end mb-4">
        <div className="flex items-center gap-2 justify-end whitespace-nowrap">
          <Select
            value={filterCategoryId}
            onChange={(e) => setFilterCategoryId(e.target.value)}
            className="!w-72 select-sm"
          >
            <option value="all">Tất cả</option>
            <option value="__default__">Mặc định</option>
            {eventCategories.map((cate) => (
              <option key={cate.id} value={cate.id}>
                {cate.name}
              </option>
            ))}
          </Select>

          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm theo mã / tên…"
            className="!w-72 input-sm"
          />

          {canCreate && (
            <button
              className="btn btn-success btn-sm whitespace-nowrap"
              onClick={openCreate}
            >
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Thêm mẫu
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500">Đang tải…</div>
      ) : (
        <TableComponent
          headers={headers}
          data={templates}
          settings={settings}
        />
      )}

      <Modal
        isOpen={editorOpen}
        title={
          editing?.id
            ? "Chỉnh sửa mẫu thiệp sự kiện"
            : "Tạo mẫu thiệp sự kiện"
        }
        onClose={closeEditor}
        footer={
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              <button
                className="btn btn-outline"
                onClick={resetSchema}
                title="Reset về mặc định"
              >
                <ArrowPathIcon className="w-5 h-5 mr-1" />
                Reset
              </button>

              <button className="btn btn-outline btn-primary" onClick={addText}>
                <PlusIcon className="w-5 h-5 mr-1" />
                Thêm chữ
              </button>

              <button className="btn btn-outline btn-info" onClick={addImage}>
                <PlusIcon className="w-5 h-5 mr-1" />
                Thêm ảnh
              </button>
            </div>

            <div className="flex gap-2">
              <button className="btn btn-ghost" onClick={closeEditor}>
                Huỷ
              </button>
              <button className="btn btn-primary" onClick={saveTemplate}>
                Lưu
              </button>
            </div>
          </div>
        }
      >
        <EventInvitationCardTemplateEditor
          schema={schema}
          setSchema={setSchema}
          selected={selected}
          setSelected={setSelected}
          metaForm={metaForm}
          setMetaForm={setMetaForm}
          categories={eventCategories}
          editing={editing}
        />
      </Modal>

      <ConfirmDeleteModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, id: null })}
        onConfirm={doDelete}
      />
    </div>
  );
};

export default EventInvitationCardTemplates;